import React, { useState } from 'react';
import { Icon, Button, Switch } from 'antd';
import classNames from 'classnames';
import Modal from '../Modal/Modal';
import MoreInfo from './MoreInfo';
import { createSanitizedHTML } from '../../utils/sanitize';
import { useSectionGuidanceVisibility } from '../../contexts/SectionGuidanceVisibilityContext';
import { getPartnerAccent } from '../../themes/partnerTheme';
import './InfoBox.scss';

export default function InfoBox({
  message,
  guidance,
  steps,
  color = getPartnerAccent('primary'),
  size = 'default',
  type = 'normal',
  layout = 'default',
  iconType = 'solution',
  more = false,
  moreBtnText = 'Learn More',
  moreModalTitle = 'Guidance',
  video,
  image,
  moreMessage,
  toggleable = false,
  defaultGuidanceOn = true,
  toggleLabel = 'Show guidance',
  honorSectionGuidanceToggle = true,
  className: classNameProp,
}) {
  const [isGuidanceOn, setIsGuidanceOn] = useState(defaultGuidanceOn);
  const sectionGuidanceCtx = useSectionGuidanceVisibility();
  const useGlobalGuidance = Boolean(sectionGuidanceCtx) && honorSectionGuidanceToggle;
  const isSectionGuidanceVisible = sectionGuidanceCtx ? sectionGuidanceCtx.isGuidanceVisible : true;

  const isFill = type === 'fill';
  const hasExpandableGuidance = Boolean(guidance || steps);
  const hasMessageBody =
    message != null &&
    (typeof message !== 'string' || message.trim().length > 0);
  const hasToggleableBody = hasExpandableGuidance || hasMessageBody;

  if (layout !== 'section' && useGlobalGuidance && !isSectionGuidanceVisible && hasToggleableBody) return null;

  if (layout === 'section') {
    if (useGlobalGuidance && !isSectionGuidanceVisible) return null;

    let sectionContent;
    if (guidance) {
      sectionContent =
        typeof guidance === 'string' ? (
          <div className="info-box__guidance" dangerouslySetInnerHTML={createSanitizedHTML(guidance)} />
        ) : (
          <div className="info-box__guidance">{guidance}</div>
        );
    } else if (typeof message === 'string') {
      sectionContent = <div className="info-box__section-message" dangerouslySetInnerHTML={createSanitizedHTML(message)} />;
    } else if (message != null) {
      sectionContent = <div className="info-box__section-message">{message}</div>;
    }

    if (!sectionContent) return null;

    const sectionIconStyle = { color };

    return (
      <div
        className={classNames('info-box', 'info-box--section', classNameProp)}
        style={{ borderLeftColor: color }}
        role="status"
      >
        <Icon type={iconType} className="info-box__section-icon" style={sectionIconStyle} aria-hidden />
        <div className="info-box__section-content">{sectionContent}</div>
      </div>
    );
  }

  const containerStyle = isFill
    ? { backgroundColor: color }
    : { borderLeftColor: color };
  const iconStyle = { color: isFill ? '#fff' : color };
  const fillBtnStyle = { borderColor: 'rgba(255,255,255,0.5)', color: '#fff', backgroundColor: 'transparent' };

  // When guidance is provided, it is the primary visible content.
  // The original message becomes the Learn More detail automatically.
  const showMore = more || (!!guidance && !!message);
  const detailContent = moreMessage || message;

  let content;
  if (guidance) {
    content = (
      typeof guidance === 'string'
        ? <div className="info-box__guidance" dangerouslySetInnerHTML={createSanitizedHTML(guidance)} />
        : <div className="info-box__guidance">{guidance}</div>
    );
  } else if (steps) {
    const stepOccurrenceByLabel = new Map();
    let anonymousStepSeq = 0;
    content = (
      <ol className="info-box__steps">
        {steps.map((step, stepIndex) => {
          const label = typeof step === 'string' || typeof step === 'number' ? String(step) : '';
          const occurrence = (stepOccurrenceByLabel.get(label) ?? 0) + 1;
          stepOccurrenceByLabel.set(label, occurrence);
          let stepKey;
          if (!label) { anonymousStepSeq += 1; stepKey = `info-box-anon-${anonymousStepSeq}`; }
          else if (occurrence === 1) stepKey = label;
          else stepKey = `${label}__${occurrence}`;
          return (
            <li key={stepKey} className="info-box__step">
              <span className="info-box__step-num" style={{ backgroundColor: color }}>{stepIndex + 1}</span>
              <span className="info-box__step-text">{step}</span>
            </li>
          );
        })}
      </ol>
    );
  } else if (typeof message === 'string') {
    content = <div dangerouslySetInnerHTML={createSanitizedHTML(message)} />;
  } else {
    content = message;
  }

  let showInner;
  if (toggleable && !useGlobalGuidance) showInner = isGuidanceOn;
  else if (useGlobalGuidance) showInner = !hasToggleableBody || isSectionGuidanceVisible;
  else showInner = true;

  return (
    <div
      className={classNames(
        'info-box',
        `info-box--${type}`,
        {
          'info-box--large': size === 'large',
          'info-box--toggleable': toggleable && !useGlobalGuidance,
          'info-box--guidance-off': toggleable && !useGlobalGuidance && !isGuidanceOn,
        },
        classNameProp
      )}
      style={{ ...containerStyle, '--info-box-accent': color }}
    >
      {toggleable && !useGlobalGuidance && (
        <div className="info-box__toggle-row">
          <Switch
            size="small"
            checked={isGuidanceOn}
            onChange={setIsGuidanceOn}
            aria-label={toggleLabel}
          />
          <span className="info-box__toggle-label">{toggleLabel}</span>
        </div>
      )}
      {showInner && (
        <div className="info-box__inner">
          <Icon type="info-circle" className="info-box__icon" style={iconStyle} />
          <div className="info-box__body">
            {showMore ? (
              <div className={classNames('info-box__content-row', { 'info-box__content-row--inline': guidance })}>
                {content}
                <Modal
                  title={
                    <span className="more-info-modal__title">
                      <Icon type="info-circle" className="more-info-modal__title-icon" aria-hidden />
                      <span>{moreModalTitle}</span>
                    </span>
                  }
                  width={video ? 720 : 600}
                  centered
                  maskClosable
                  keyboard
                  wrapClassName={classNames('more-info-modal', { 'more-info-modal--with-video': !!video })}
                  className="more-info-modal__dialog modal"
                  // Prevent focus restore scroll-jump while the overlay is tearing down
                  focusTriggerAfterClose={false}
                  toggle={(show) => (
                    <Button
                      type="default"
                      size="small"
                      onClick={show}
                      className={classNames('info-box__btn', { 'info-box__btn--on-fill': isFill })}
                      style={isFill ? fillBtnStyle : undefined}
                    >
                      {moreBtnText}
                    </Button>
                  )}
                >
                  {() => <MoreInfo video={video} image={image} message={detailContent} />}
                </Modal>
              </div>
            ) : (
              content
            )}
          </div>
        </div>
      )}
    </div>
  );
}
